import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

function AddProduct() {
  const getDefaultProduct = () => ({
    name: "",
    price: "",
    description: "",
    category: "",
    subCategory: "",
    expectedDeliveryTime: "",
    minQuantity: 1,
    isDieShape: false,
    isPaperType: false,
    isFlapOpening: false,
    isWindowCutting: false,
    isPrinting: false,
    isLaminationType: false,
    videoUrl: "",
    priceTiers: [{ quantity: 1, price: "" }],
    paperTypes: [{ name: "", price: "" }],
    flapOpenings: [{ name: "", price: "" }],
    windowCuttings: [{ name: "", price: "" }],
    printings: [{ name: "", price: "" }],
    laminationTypes: [{ name: "", price: "" }],
  });

  const [product, setProduct] = useState(getDefaultProduct());
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [subCategoryNames, setSubCategoryNames] = useState({});
  const [images, setImages] = useState([]);
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [editingProductId, setEditingProductId] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    axios
      .get("https://api.vyapakprint.com/api/products/getProducts")
      .then((response) => {
        setProducts(response.data);
        fetchSubCategoryNames(response.data);
      })
      .catch((error) => {
        console.error("Error fetching products:", error);
      });
  }, []);

  useEffect(() => {
    axios
      .get("https://api.vyapakprint.com/api/categories/getCategories")
      .then((response) => {
        setCategories(response.data);
      })
      .catch((error) => {
        console.error("Error fetching categories:", error);
      });
  }, []);

  const fetchSubCategoryNames = async (products) => {
    const subCategoryIds = products
      .map((product) => product.subCategory)
      .filter(Boolean);

    const uniqueSubCategoryIds = [...new Set(subCategoryIds)];

    const names = await Promise.all(
      uniqueSubCategoryIds.map(async (id) => {
        try {
          const response = await axios.get(
            `https://api.vyapakprint.com/api/categories/subcategories/${id}`,
          );
          return { id, name: response.data.name };
        } catch (error) {
          console.error(`Error fetching subcategory name for ID ${id}:`, error);
          return { id, name: "N/A" };
        }
      }),
    );

    const subCategoryNamesMap = names.reduce(
      (acc, { id, name }) => ({ ...acc, [id]: name }),
      {},
    );

    setSubCategoryNames(subCategoryNamesMap);
  };

  const normalizeAddonArray = (arr) => {
    if (!Array.isArray(arr) || arr.length === 0) {
      return [{ name: "", price: "" }];
    }

    return arr.map((item) => {
      if (typeof item === "string") {
        return { name: item, price: "" };
      }

      return {
        name: item?.name || "",
        price: item?.price ?? "",
      };
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setProduct((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (name === "category") {
      const selectedCategory = categories.find(
        (category) => category._id === value,
      );

      setSubCategories(selectedCategory ? selectedCategory.subcategory : []);

      setProduct((prevProduct) => ({
        ...prevProduct,
        category: value,
        subCategory: "",
      }));
    }
  };

  const handleImageChange = (e) => {
    setImages(Array.from(e.target.files));
  };

  const handleAddonChange = (fieldName, index, key, value) => {
    setProduct((prev) => {
      const updated = [...prev[fieldName]];
      updated[index] = {
        ...updated[index],
        [key]: value,
      };
      return {
        ...prev,
        [fieldName]: updated,
      };
    });
  };

  const addAddonField = (fieldName) => {
    setProduct((prev) => ({
      ...prev,
      [fieldName]: [...prev[fieldName], { name: "", price: "" }],
    }));
  };

  const removeAddonField = (fieldName, index) => {
    setProduct((prev) => {
      const filtered = prev[fieldName].filter((_, i) => i !== index);
      return {
        ...prev,
        [fieldName]: filtered.length ? filtered : [{ name: "", price: "" }],
      };
    });
  };

  const handlePriceTierChange = (index, e) => {
    const { name, value } = e.target;
    const newPriceTiers = [...product.priceTiers];
    newPriceTiers[index][name] = value;
    setProduct({ ...product, priceTiers: newPriceTiers });
  };

  const addPriceTier = () => {
    setProduct({
      ...product,
      priceTiers: [...product.priceTiers, { quantity: 1, price: "" }],
    });
  };

  const removePriceTier = (index) => {
    const newPriceTiers = product.priceTiers.filter((_, i) => i !== index);
    setProduct({
      ...product,
      priceTiers: newPriceTiers.length
        ? newPriceTiers
        : [{ quantity: 1, price: "" }],
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!product.name) {
      alert("Please fill in the Name field.");
      return;
    }

    if (!product.description) {
      alert("Please fill in the Description field.");
      return;
    }

    if (!product.expectedDeliveryTime) {
      alert("Please fill in the Expected Delivery Time field.");
      return;
    }

    setLoading(true);

    const formData = new FormData();
    formData.append("name", product.name);
    formData.append("description", product.description);
    formData.append("category", product.category);
    formData.append("subCategory", product.subCategory);
    formData.append("expectedDeliveryTime", product.expectedDeliveryTime);
    formData.append("minimumQuantity", product.minQuantity);
    formData.append("isDieShape", product.isDieShape);
    formData.append("isPaperType", product.isPaperType);
    formData.append("isWindowCutting", product.isWindowCutting);
    formData.append("isFlapOpening", product.isFlapOpening);
    formData.append("isPrinting", product.isPrinting);
    formData.append("isLaminationType", product.isLaminationType);
    formData.append("priceTiers", JSON.stringify(product.priceTiers));
    formData.append("paperTypes", JSON.stringify(product.paperTypes));
    formData.append("windowCuttings", JSON.stringify(product.windowCuttings));
    formData.append("flapOpenings", JSON.stringify(product.flapOpenings));
    formData.append("printings", JSON.stringify(product.printings));
    formData.append("laminationTypes", JSON.stringify(product.laminationTypes));
    formData.append("videoUrl", product.videoUrl);

    images.forEach((image) => {
      formData.append("images", image);
    });

    try {
      if (isEditing) {
        await axios.put(
          `https://api.vyapakprint.com/api/products/updateProduct/${editingProductId}`,
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          },
        );
        alert("Product updated successfully");
        setIsEditing(false);
        setEditingProductId(null);
      } else {
        await axios.post(
          "https://api.vyapakprint.com/api/products/createProduct",
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          },
        );
        alert("Product added successfully");
      }

      setProduct(getDefaultProduct());
      setImages([]);
      setSubCategories([]);

      const response = await axios.get(
        "https://api.vyapakprint.com/api/products/getProducts",
      );
      setProducts(response.data);
      fetchSubCategoryNames(response.data);
    } catch (error) {
      console.error("Error adding/updating product:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this Product?")) {
      try {
        await axios.delete(
          `https://api.vyapakprint.com/api/products/deleteProduct/${id}`,
        );
        alert("Product deleted successfully");
        const updatedProducts = products.filter(
          (product) => product._id !== id,
        );
        setProducts(updatedProducts);
      } catch (error) {
        console.error("Error deleting product:", error);
      }
    }
  };

  async function getSubCategoryNameById(subCategoryId) {
    if (!subCategoryId) {
      return "N/A";
    }

    try {
      const response = await fetch(
        `your-api-url/subcategories/${subCategoryId}`,
      );

      if (!response.ok) {
        throw new Error("Failed to fetch subcategory name");
      }

      const data = await response.json();
      return data.name;
    } catch (error) {
      console.error("Error fetching subcategory name:", error);
      return "N/A";
    }
  }

  const handleEdit = (product) => {
    window.scrollTo(0, 0);

    setIsEditing(true);
    setEditingProductId(product._id);

    const categoryId =
      typeof product.category === "object" && product.category?._id
        ? product.category._id
        : product.category || "";

    const selectedCategory = categories.find((cat) => cat._id === categoryId);
    setSubCategories(selectedCategory ? selectedCategory.subcategory : []);

    setProduct({
      name: product.name || "",
      price: product.price || "",
      description: product.description || "",
      category: categoryId,
      subCategory: product.subCategory || "",
      expectedDeliveryTime: product.expectedDeliveryTime || "",
      minQuantity: product.minimumQuantity || 1,
      isDieShape: !!product.isDieShape,
      isPaperType: !!product.isPaperType,
      isWindowCutting: !!product.isWindowCutting,
      isFlapOpening: !!product.isFlapOpening,
      isPrinting: !!product.isPrinting,
      isLaminationType: !!product.isLaminationType,
      paperTypes: normalizeAddonArray(product.paperTypes),
      windowCuttings: normalizeAddonArray(product.windowCuttings),
      flapOpenings: normalizeAddonArray(product.flapOpenings),
      printings: normalizeAddonArray(product.printings),
      laminationTypes: normalizeAddonArray(product.laminationTypes),
      videoUrl: product.videoUrl || "",
      priceTiers:
        Array.isArray(product.priceTiers) && product.priceTiers.length
          ? product.priceTiers
          : [{ quantity: 1, price: "" }],
    });

    setImages([]);
  };

  const handleAdd = (product) => {
    window.scrollTo(0, 0);

    const categoryId =
      typeof product.category === "object" && product.category?._id
        ? product.category._id
        : product.category || "";

    const selectedCategory = categories.find((cat) => cat._id === categoryId);
    setSubCategories(selectedCategory ? selectedCategory.subcategory : []);

    setProduct({
      name: product.name || "",
      price: product.price || "",
      description: product.description || "",
      category: categoryId,
      subCategory: product.subCategory || "",
      expectedDeliveryTime: product.expectedDeliveryTime || "",
      minQuantity: product.minimumQuantity || 1,
      isDieShape: !!product.isDieShape,
      isPaperType: !!product.isPaperType,
      isWindowCutting: !!product.isWindowCutting,
      isFlapOpening: !!product.isFlapOpening,
      isPrinting: !!product.isPrinting,
      isLaminationType: !!product.isLaminationType,
      paperTypes: normalizeAddonArray(product.paperTypes),
      windowCuttings: normalizeAddonArray(product.windowCuttings),
      flapOpenings: normalizeAddonArray(product.flapOpenings),
      printings: normalizeAddonArray(product.printings),
      laminationTypes: normalizeAddonArray(product.laminationTypes),
      videoUrl: product.videoUrl || "",
      priceTiers:
        Array.isArray(product.priceTiers) && product.priceTiers.length
          ? product.priceTiers
          : [{ quantity: 1, price: "" }],
    });

    setImages([]);
    setIsEditing(false);
    setEditingProductId(null);
  };

  const filteredProducts = products
    .filter((product) => {
      const nameMatch = product.name
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase());

      const categoryMatch = product?.category?.name
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase());

      const subcategoryMatch = subCategoryNames[product.subCategory]
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase());

      return nameMatch || categoryMatch || subcategoryMatch;
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  const clearForm = () => {
    setProduct(getDefaultProduct());
    setImages([]);
    setIsEditing(false);
    setEditingProductId(null);
    setSubCategories([]);
  };

  const handleView = (id) => {
    navigate(`/product/${id}`);
  };

  const renderAddonSection = (
    label,
    enabledField,
    fieldName,
    placeholderText,
    addButtonText,
  ) => (
    <>
      <div className="flex items-center">
        <label className="mr-2" htmlFor={enabledField}>
          {label}
        </label>
        <input
          type="checkbox"
          id={enabledField}
          name={enabledField}
          checked={product[enabledField]}
          onChange={handleChange}
          className="mr-2 leading-tight"
        />
      </div>

      {product[enabledField] && (
        <div className="space-y-2">
          {product[fieldName].map((item, index) => (
            <div key={index} className="flex items-center space-x-2">
              <input
                type="text"
                value={item.name}
                onChange={(e) =>
                  handleAddonChange(fieldName, index, "name", e.target.value)
                }
                placeholder={placeholderText}
                className="w-full p-2 border rounded-md focus:outline-none"
              />

              <input
                type="number"
                value={item.price}
                onChange={(e) =>
                  handleAddonChange(fieldName, index, "price", e.target.value)
                }
                placeholder="Add price"
                className="w-48 p-2 border rounded-md focus:outline-none"
              />

              {index > 0 && (
                <button
                  type="button"
                  onClick={() => removeAddonField(fieldName, index)}
                  className="bg-red-500 text-white p-2 rounded-md"
                >
                  Remove
                </button>
              )}
            </div>
          ))}

          <button
            type="button"
            onClick={() => addAddonField(fieldName)}
            className="bg-blue-500 text-white p-2 rounded-md"
          >
            {addButtonText}
          </button>
        </div>
      )}
    </>
  );

  return (
    <div>
      <div className="max-w-full flex justify-center items-center p-4">
        <div className="bg-white shadow-lg rounded-lg p-8 w-full max-w-full">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">
            {isEditing ? "Edit Product" : "Add Product"}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <select
                id="category"
                name="category"
                value={product.category}
                onChange={handleChange}
                className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-gray-800"
              >
                <option value="">Select category (Optional)</option>
                {categories.map((category) => (
                  <option key={category._id} value={category._id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <select
                id="subCategory"
                name="subCategory"
                value={product.subCategory}
                onChange={handleChange}
                className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-gray-800"
                disabled={!subCategories.length}
              >
                <option value="">Select subcategory (Optional)</option>
                {subCategories.map((subCategory) => (
                  <option key={subCategory._id} value={subCategory._id}>
                    {subCategory.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <input
                type="text"
                id="name"
                name="name"
                value={product.name}
                onChange={handleChange}
                className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-gray-800"
                placeholder="Enter product name"
              />
            </div>

            <div>
              <textarea
                id="description"
                name="description"
                value={product.description}
                onChange={handleChange}
                className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-gray-800"
                placeholder="Enter product description"
              />
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <div className="flex-1 min-w-[220px]">
                <input
                  type="text"
                  id="expectedDeliveryTime"
                  name="expectedDeliveryTime"
                  value={product.expectedDeliveryTime}
                  onChange={handleChange}
                  className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-gray-800"
                  placeholder="Enter expected delivery time"
                />
              </div>

              <div className="flex-1 min-w-[220px]">
                <input
                  type="number"
                  id="minQuantity"
                  name="minQuantity"
                  value={product.minQuantity}
                  onChange={handleChange}
                  className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-gray-800"
                  placeholder="Enter minimum quantity"
                />
              </div>

              <div className="flex items-center">
                <label className="mr-2" htmlFor="isDieShape">
                  Is Die Shape?
                </label>
                <input
                  type="checkbox"
                  id="isDieShape"
                  name="isDieShape"
                  checked={product.isDieShape}
                  onChange={handleChange}
                  className="mr-2 leading-tight"
                />
              </div>

              <div className="flex-1 min-w-[220px]">
                <input
                  type="file"
                  name="images"
                  multiple
                  onChange={handleImageChange}
                  className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-gray-800"
                />
              </div>
            </div>

            {renderAddonSection(
              "Is Paper Type?",
              "isPaperType",
              "paperTypes",
              "Enter paper type",
              "Add Paper Type",
            )}

            {renderAddonSection(
              "Is Window Cutting?",
              "isWindowCutting",
              "windowCuttings",
              "Enter window cutting",
              "Add Window Cutting",
            )}

            {renderAddonSection(
              "Is Flap Opening?",
              "isFlapOpening",
              "flapOpenings",
              "Enter flap opening",
              "Add Flap Opening",
            )}

            {renderAddonSection(
              "Is Printing?",
              "isPrinting",
              "printings",
              "Enter printing type",
              "Add Printing",
            )}

            {renderAddonSection(
              "Is Lamination Type?",
              "isLaminationType",
              "laminationTypes",
              "Enter lamination type",
              "Add Lamination Type",
            )}

            <div>
              <input
                type="text"
                id="videoUrl"
                name="videoUrl"
                value={product.videoUrl}
                onChange={handleChange}
                className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-gray-800"
                placeholder="Enter video URL"
              />
            </div>

            <div>
              {product.priceTiers.map((tier, index) => (
                <div key={index} className="flex space-x-4 mb-2">
                  <input
                    type="number"
                    name="quantity"
                    placeholder="Quantity"
                    value={tier.quantity}
                    onChange={(e) => handlePriceTierChange(index, e)}
                    className="w-1/2 p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-gray-800"
                  />
                  <input
                    type="number"
                    name="price"
                    placeholder="Price"
                    value={tier.price}
                    onChange={(e) => handlePriceTierChange(index, e)}
                    className="w-1/2 p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-gray-800"
                  />
                  {index > 0 && (
                    <button
                      type="button"
                      onClick={() => removePriceTier(index)}
                      className="bg-red-500 text-white p-2 rounded-md"
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}

              <button
                type="button"
                onClick={addPriceTier}
                className="bg-blue-500 text-white p-2 rounded-md"
              >
                Add Price Tier
              </button>
            </div>

            <div className="flex items-center gap-4">
              <button
                type="submit"
                className="w-auto px-6 py-3 bg-gray-800 text-white font-bold rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-800"
                disabled={loading}
              >
                {loading
                  ? "Submitting..."
                  : isEditing
                    ? "Update Product"
                    : "Add Product"}
              </button>

              {isEditing && (
                <button
                  type="button"
                  onClick={clearForm}
                  className="bg-gray-500 hover:bg-gray-400 text-white py-3 px-4 font-bold rounded-md"
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>
      </div>

      <div className="container mx-auto p-4">
        <h2 className="text-2xl font-bold mb-4">Product Table</h2>

        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by name, category, or subcategory..."
          className="mb-4 p-2 border border-gray-300 rounded-md w-full"
        />

        <table className="min-w-full table-auto text-center">
          <thead>
            <tr className="bg-gray-800 text-white">
              <th className="p-2">Sr No.</th>
              <th className="p-2">Name</th>
              <th className="p-2">Category</th>
              <th className="p-2">Subcategory</th>
              <th className="p-2">Date</th>
              <th className="p-2">Actions</th>
            </tr>
          </thead>

          <tbody>
            {filteredProducts.map((product, index) => (
              <tr key={product._id} className="border-t">
                <td className="p-2">{index + 1}</td>
                <td className="p-2 text-start">{product.name}</td>
                <td className="p-2">{product?.category?.name || "Generic"}</td>
                <td className="p-2">
                  {subCategoryNames[product.subCategory] || "N/A"}
                </td>
                <td className="py-2">
                  {new Date(product.createdAt).toLocaleDateString()}
                </td>
                <td className="p-2 space-x-4">
                  <button
                    onClick={() => handleAdd(product)}
                    className="bg-gray-800 hover:bg-gray-700 text-white p-2 rounded-md"
                  >
                    Add
                  </button>

                  <button
                    onClick={() => handleView(product._id)}
                    className="bg-blue-500 hover:bg-blue-700 text-white p-2 rounded-md"
                  >
                    View
                  </button>

                  <button
                    onClick={() => handleEdit(product)}
                    className="bg-yellow-400 hover:bg-yellow-600 text-white p-2 rounded-md"
                  >
                    Edit
                  </button>

                  <button
                    onClick={() => handleDelete(product._id)}
                    className="bg-red-500 hover:bg-red-700 text-white p-2 rounded-md"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default AddProduct;
